import { useEffect, useRef } from 'react';

const EVENT_NAME = 'bloom:chartScrollSync';
const STORE_KEY = '__bloomChartScrollSync';

/**
 * Last scroll position recorded for a sync group, or null if nothing has
 * scrolled it yet. Lets a chart tell "the user has a position here already"
 * apart from "this group is untouched" before applying an opening position.
 */
export function getSyncedChartScrollLeft(groupId = 'price-pulse') {
  const g = typeof globalThis !== 'undefined' ? globalThis : null;
  const last = g?.[STORE_KEY]?.[groupId];
  return typeof last === 'number' && !Number.isNaN(last) ? last : null;
}

/**
 * Sync horizontal scrolling across multiple scroll containers.
 *
 * Usage:
 * - const scrollRef = useRef(null)
 * - useSyncedChartScroll(scrollRef, 'price-pulse')
 * - <div ref={scrollRef} ... />
 */
export function useSyncedChartScroll(scrollContainerRef, groupId = 'price-pulse') {
  const sourceIdRef = useRef(
    typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`
  );
  const isApplyingRemoteRef = useRef(false);
  const attachedElRef = useRef(null);

  const getStore = () => {
    const g = typeof globalThis !== 'undefined' ? globalThis : null;
    if (!g) return null;
    if (!g[STORE_KEY]) g[STORE_KEY] = {};
    return g[STORE_KEY];
  };

  useEffect(() => {
    let rafId = 0;
    let cleanup = null;
    let cancelled = false;

    const attach = (el) => {
      if (!el) return;
      if (attachedElRef.current === el) return;
      if (cleanup) cleanup();
      attachedElRef.current = el;

      // On attach, apply last known scroll position for this group (if any)
      const store = getStore();
      const last = store?.[groupId];
      if (typeof last === 'number' && !Number.isNaN(last) && Math.abs(el.scrollLeft - last) >= 0.5) {
        isApplyingRemoteRef.current = true;
        el.scrollLeft = last;
        requestAnimationFrame(() => {
          isApplyingRemoteRef.current = false;
        });
      }

      const onScroll = () => {
        if (isApplyingRemoteRef.current) return;
        const s = getStore();
        if (s) s[groupId] = el.scrollLeft;
        window.dispatchEvent(
          new CustomEvent(EVENT_NAME, {
            detail: {
              groupId,
              sourceId: sourceIdRef.current,
              scrollLeft: el.scrollLeft,
            },
          })
        );
      };

      el.addEventListener('scroll', onScroll, { passive: true });
      cleanup = () => {
        el.removeEventListener('scroll', onScroll);
        if (attachedElRef.current === el) attachedElRef.current = null;
      };
    };

    const tick = () => {
      if (cancelled) return;
      const el = scrollContainerRef?.current;
      if (el) {
        attach(el);
        return;
      }
      rafId = requestAnimationFrame(tick);
    };

    tick();

    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      if (cleanup) cleanup();
    };
  }, [scrollContainerRef, groupId]);

  useEffect(() => {
    const onSync = (evt) => {
      const el = scrollContainerRef?.current;
      if (!el) return;

      const detail = evt?.detail;
      if (!detail || detail.groupId !== groupId) return;
      if (detail.sourceId === sourceIdRef.current) return;
      if (typeof detail.scrollLeft !== 'number') return;

      if (Math.abs(el.scrollLeft - detail.scrollLeft) < 0.5) return;

      isApplyingRemoteRef.current = true;
      el.scrollLeft = detail.scrollLeft;
      const s = getStore();
      if (s) s[groupId] = detail.scrollLeft;
      // next tick allow local scroll handler again
      requestAnimationFrame(() => {
        isApplyingRemoteRef.current = false;
      });
    };

    window.addEventListener(EVENT_NAME, onSync);
    return () => window.removeEventListener(EVENT_NAME, onSync);
  }, [scrollContainerRef, groupId]);
}

